import { useEffect, useState } from "react";
import { Play, Music2, Video, FileText, Radio, Sliders, GraduationCap, ShoppingBag } from "lucide-react";
import api from "@/lib/api";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Overline } from "@/components/Bits";
import CheckoutDialog from "@/components/CheckoutDialog";
import { EditorialMarquee } from "@/components/EditorialMarquee";

const OFFERINGS = [
  { icon: Music2, title: "Original Albums" },
  { icon: Video, title: "Worship Videos" },
  { icon: FileText, title: "Song Lyrics" },
  { icon: Radio, title: "Live Worship Sessions" },
  { icon: Sliders, title: "Music Production" },
  { icon: GraduationCap, title: "Worship Training" },
];

export default function Music() {
  const [tracks, setTracks] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { type: "music" } }).then(({ data }) => setTracks(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="music-page">
      <PageHero
        overline="Music Ministry"
        title={<>Worship that inspires <span className="font-accent text-gold-gradient italic">transformation.</span></>}
        subtitle="Through worship, songwriting, and musical excellence, lives are encouraged, hearts are restored, and people are drawn into God's presence."
      />

      {/* Discography */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-24">
        <Reveal className="mb-14"><Overline>Discography</Overline></Reveal>
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
          {tracks.map((t, i) => (
            <Reveal key={t.slug} delay={(i % 3) * 0.07}>
              <div className="group flex h-full flex-col" data-testid={`track-${t.slug}`}>
                <div className="relative overflow-hidden border border-gold-500/20 aspect-square">
                  <img src={t.image} alt={t.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-ink/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gold-500 text-ink">
                      <Play size={24} className="ml-1" fill="currentColor" />
                    </span>
                  </div>
                  <span className="absolute left-3 top-3 bg-ink/80 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-gold-400 border border-gold-500/30">
                    {t.category}
                  </span>
                </div>
                <h3 className="mt-5 font-serif text-2xl text-white">{t.title}</h3>
                <p className="mt-2 text-sm text-white/60 leading-relaxed flex-1">{t.description}</p>
                <div className="mt-5 flex items-center justify-between">
                  <span className="font-serif text-2xl text-gold-400">${t.price}</span>
                  <CheckoutDialog item={{ type: "product", slug: t.slug, title: t.title, price: t.price }}>
                    <button data-testid={`buy-${t.slug}`} className="inline-flex items-center gap-2 border border-gold-500/40 px-5 py-2.5 text-xs text-white/85 hover:border-gold-400 hover:text-gold-300 transition-colors duration-300">
                      <ShoppingBag size={15} /> Get
                    </button>
                  </CheckoutDialog>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <EditorialMarquee text="DRAWN INTO HIS PRESENCE" />

      {/* Temporary YouTube worship playlist — will be replaced when Apostle's albums drop */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-24" data-testid="worship-playlist">
        <Reveal className="mb-10 flex flex-wrap items-end justify-between gap-6">
          <div>
            <Overline>Worship Playlist · While You Wait</Overline>
            <h2 className="mt-4 font-serif text-3xl md:text-5xl text-white leading-tight max-w-2xl">
              Songs to <span className="font-accent italic text-gold-gradient">draw you in.</span>
            </h2>
            <p className="mt-3 text-sm text-white/55 max-w-xl">
              A curated worship set to soak in while our own recordings — Throne Room, He Reigns, Kingdom Come — are prepared. Coming soon.
            </p>
          </div>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[
            { id: "n0FBb6hnwTo", title: "The Goodness of God", artist: "Bethel Music" },
            { id: "nQWFzMvCfLE", title: "What A Beautiful Name", artist: "Hillsong Worship" },
            { id: "dy9nwe9_xzw", title: "Oceans — Live Session", artist: "Hillsong UNITED" },
            { id: "x_UK15f9KxY", title: "Promise Keeper", artist: "Nathaniel Bassey ft. Mercy Chinwo" },
            { id: "ft5tScISY90", title: "YAH", artist: "Dunsin Oyekan" },
            { id: "YA2Lxfw4SSw", title: "Too Faithful — Live", artist: "Moses Bliss" },
          ].map((v, i) => (
            <Reveal key={v.id} delay={(i % 3) * 0.05}>
              <div className="border border-gold-500/20 bg-ink-paper/40 overflow-hidden" data-testid={`worship-video-${v.id}`}>
                <div className="aspect-video bg-black">
                  <iframe
                    className="w-full h-full"
                    src={`https://www.youtube.com/embed/${v.id}?rel=0`}
                    title={v.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
                <div className="p-5">
                  <p className="font-serif text-lg text-white">{v.title}</p>
                  <p className="text-xs text-gold-400 mt-1 uppercase tracking-[0.15em]">{v.artist}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10 text-center">
          <p className="text-sm text-white/50 max-w-md mx-auto italic">
            Our own worship albums are being prepared — join the newsletter to be the first to hear.
          </p>
        </Reveal>
      </section>

      {/* Offerings */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-24">
        <Reveal className="mb-14"><Overline>Music Resources</Overline></Reveal>
        <div className="grid gap-px bg-gold-500/15 border border-gold-500/15 sm:grid-cols-2 lg:grid-cols-3">
          {OFFERINGS.map((o, i) => (
            <Reveal key={o.title} delay={(i % 3) * 0.05}>
              <div className="flex items-center gap-5 bg-ink p-8 hover:bg-ink-paper transition-colors duration-500">
                <o.icon size={24} className="text-gold-500" strokeWidth={1.4} />
                <span className="font-serif text-xl text-white">{o.title}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
